import { Component, inject, input, OnChanges, SecurityContext, signal, SimpleChanges } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

/**
 * The rich-text toolbar buttons textAngular renders for the html-mode `ta-toolbar`
 * `[['bold','italics','underline','insertLink','ul','ol','html']]` (captured from the frozen
 * baseline, _ta-oracle-terms.html). Empty in text mode (v1 htmlField.js `toolbar = '[[]]'`).
 */
const HTML_TOOLBAR_BUTTONS: ReadonlyArray<{ name: string; title: string; icon: string }> = [
    { name: 'bold', title: 'Bold', icon: 'fa fa-bold' },
    { name: 'italics', title: 'Italic', icon: 'fa fa-italic' },
    { name: 'underline', title: 'Underline', icon: 'fa fa-underline' },
    { name: 'insertLink', title: 'Insert / edit link', icon: 'fa fa-link' },
    { name: 'ul', title: 'Unordered List', icon: 'fa fa-list-ul' },
    { name: 'ol', title: 'Ordered List', icon: 'fa fa-list-ol' },
    { name: 'html', title: 'Toggle html / Rich Text', icon: 'fa fa-code' },
];

/**
 * Native port of the AngularJS `ftHtmlField` field-editor directive in plain-text mode
 * (`mode='text'`): a textAngular-in-text-mode editor for token-bearing text fields
 * (contest share messages, etc.). Per language: a `.ta-toolbar` with an "Insert Token"
 * dropdown, then a `.field` with the contenteditable `.ta-bind` wrapper. Typing writes
 * plain text back to `fieldData[field.name]` for the host save.
 */
@Component({
    selector: 'ft-html-field-ng',
    imports: [],
    templateUrl: './ft-html-field.component.html',
    host: {
        // v1 ft-click-class closed the Insert-Token dropdown on any outside click.
        '(document:click)': 'closeTokenMenu()',
        // textAngular refreshed the toolbar buttons' active state on every selection move.
        '(document:selectionchange)': 'refreshCommandStates()',
    },
})
export class FtHtmlFieldComponent implements OnChanges {
    /** The array/object whose html fields the editors edit (array → multi-language). */
    readonly datasource = input<any>();
    /** A field name, or `{name,label}`, or a list of either — normalised below. */
    readonly fields = input<any>();
    /** Raw fieldsOptions (mode/tokens/available-tags/…); only `mode` matters for the text-mode port. */
    readonly fieldsOptions = input<any>();

    private readonly rtlLanguages = ['ar', 'he'];

    /** Editor mode: 'text' (plain-text textAngular, value wrapped in <p>) or 'html' (rich WYSIWYG). */
    protected readonly mode = signal<'text' | 'html'>('html');
    /** Whether the Insert-Token dropdown is appended (fieldsOptions.tokens present). */
    protected readonly hasTokens = signal(false);
    /** The token list for the dropdown — strings or `{text, wrapper}` objects (v1 shape). */
    protected readonly tokens = signal<any[]>([]);
    /** Insert-Token dropdown open state (v1 `ft-click-class toggle-class="active"`). */
    protected readonly tokenMenuOpen = signal(false);
    /** The rich-toolbar buttons (v1 htmlField.js: empty in text mode; bold/italics/… in html mode). */
    protected readonly toolbarButtons = signal<Array<{ name: string; title: string; icon: string }>>([]);
    /** v1 htmlField.html top `<div class="modal-panel" ng-if="readonlytext"><p ng-bind-html>` default-text panel. */
    protected readonly readonlytextHtml = signal<string | null>(null);

    protected readonly isMultiLanguage = signal(false);
    protected readonly languages = signal<any[]>([]);
    /** Per language: the live fieldData + a FROZEN initial innerHTML per field (set ONCE at open).
     * `sourceView` = the textAngular raw-html toggle state for the row's editor. */
    protected readonly rows = signal<Array<{ language: any; fieldData: any; html: Record<string, string>; sourceView?: boolean }>>([]);
    protected readonly normalizedFields = signal<Array<{ name: string; label: string | null }>>([]);

    private readonly sanitizer = inject(DomSanitizer);

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['datasource']) {
            const datasource = this.datasource();
            this.isMultiLanguage.set(Array.isArray(datasource));
            this.languages.set(this.isMultiLanguage() ? datasource : []);
            this.rows.set(this.languages().map((l: any) => ({ language: l, fieldData: l && l.data ? l.data : l, html: {} })));
        }
        if (changes['fields']) {
            const fields = this.fields();
            const raw = Array.isArray(fields) ? fields : fields == null ? [] : [fields];
            this.normalizedFields.set(raw.map((f: any) => (typeof f === 'string' ? { name: f, label: null } : f)));
        }
        if (changes['fieldsOptions']) {
            const o = this.fieldsOptions() || {};
            this.mode.set(o['mode'] === 'text' ? 'text' : 'html');
            this.hasTokens.set(!!(o['tokens'] && (Array.isArray(o['tokens']) ? o['tokens'].length : true)));
            this.tokens.set(Array.isArray(o['tokens']) ? o['tokens'] : []);
            this.toolbarButtons.set(this.mode() === 'text' ? [] : (HTML_TOOLBAR_BUTTONS as any));
            const rt = o['readonlytext'];
            this.readonlytextHtml.set(rt ? String(rt) : null);
        }
        // (Re)compute the FROZEN initial contenteditable html per row×field. Binding [innerHTML] to this
        // frozen value (NOT the live model) is essential: re-rendering [innerHTML] on each (input) event
        // resets the contenteditable caret to the start (typed chars land at the front). onInput updates
        // the live model for the host save WITHOUT touching `html`, so the caret is preserved.
        for (const row of this.rows()) {
            row.html = {};
            for (const f of this.normalizedFields()) row.html[f.name] = this.taHtml(row.fieldData[f.name]);
        }
    }

    /**
     * textAngular renders the stored value inside a `<p>` (empty → `<p><br></p>`). For mode=text the
     * value is plain text, so this matches the captured baseline DOM (`<p>im finalist…</p>`).
     *
     * 🚨 Not bypassed (FTK-7642): a stored value goes straight into a contenteditable here, so a
     * payload another user saved executed the moment the editor opened.
     */
    taHtml(value: any): string {
        const v = value == null ? '' : String(value);
        if (!v.length) return '<p><br></p>';
        // mode=text: textAngular wraps plain text in <p>. mode=html: value is already html → render as-is.
        return this.mode() === 'text' ? '<p>' + v + '</p>' : v;
    }

    /** Write the contenteditable back to the live model (host save reads it). mode=text → plain text. */
    protected onInput(ev: Event, fieldData: any, name: string): void {
        this.writeBack(ev.target as HTMLElement, fieldData, name);
    }

    /**
     * Deliberately does not sanitise (FTK-7642): this fires per keystroke, and the XSS is closed at
     * the render sinks instead — which also covers rows a write-path sanitiser can never reach.
     */
    private writeBack(el: HTMLElement, fieldData: any, name: string): void {
        fieldData[name] = this.mode() === 'text' ? el.textContent || '' : el.innerHTML;
    }

    // ---- Insert-Token dropdown (v1 textAngularTools.js ftAppendInsertTokenTool) ----

    protected toggleTokenMenu(ev: MouseEvent): void {
        ev.stopPropagation();
        this.tokenMenuOpen.set(!this.tokenMenuOpen());
    }

    protected closeTokenMenu(): void {
        if (this.tokenMenuOpen()) this.tokenMenuOpen.set(false);
    }

    // ---- Rich-text toolbar (v1 textAngular default tools; execCommand like taExecCommand) ----

    /** Active command states for the visible toolbar (bold/italics/underline/ul/ol). */
    protected readonly commandStates = signal<Record<string, boolean>>({});

    protected onToolbarButton(ev: MouseEvent, name: string, row: { fieldData: any; sourceView?: boolean }): void {
        ev.preventDefault();
        const panel = (ev.target as HTMLElement).closest('.modal-panel-input');
        const editor = panel?.querySelector<HTMLElement>('.ta-bind[contenteditable]');
        const source = panel?.querySelector<HTMLTextAreaElement>('textarea.ta-html');
        const fieldName = this.normalizedFields()[0]?.name;
        if (!editor || !fieldName) return;
        // v1 textAngular disabled every tool except `html` while the raw-source view was on.
        if (row.sourceView && name !== 'html') return;
        switch (name) {
            case 'bold': document.execCommand('bold'); break;
            case 'italics': document.execCommand('italic'); break;
            case 'underline': document.execCommand('underline'); break;
            case 'ul': document.execCommand('insertUnorderedList'); break;
            case 'ol': document.execCommand('insertOrderedList'); break;
            case 'insertLink': {
                // Verbatim textAngular default: $window.prompt seeded with 'http://'; no-op on cancel.
                const url = window.prompt('Please enter a URL to insert', 'http://');
                if (url && url !== 'http://') document.execCommand('createLink', false, url);
                break;
            }
            case 'html': {
                if (!source) return;
                if (!row.sourceView) {
                    source.value = editor.innerHTML;
                    row.sourceView = true;
                } else {
                    // 🚨 The one place in this component where a string becomes live markup, and the
                    // sink CodeQL flagged (FTK-7642). Angular's sanitiser, not DOMPurify, so the
                    // editor and the read views apply one allow-list.
                    editor.innerHTML = this.sanitizer.sanitize(SecurityContext.HTML, source.value) ?? '';
                    row.sourceView = false;
                    this.writeBack(editor, row.fieldData, fieldName);
                }
                break;
            }
        }
        // execCommand mutations fire the contenteditable's `input` event in Chromium, but write
        // back explicitly anyway — cheap, and keeps the model right if that ever changes.
        if (name !== 'html' && !row.sourceView) this.writeBack(editor, row.fieldData, fieldName);
        this.refreshCommandStates();
    }

    /** Raw-source textarea edits write the html straight to the model (v1 ta-html binding). */
    protected onSourceInput(ev: Event, row: { fieldData: any }): void {
        const name = this.normalizedFields()[0]?.name;
        if (name) row.fieldData[name] = (ev.target as HTMLTextAreaElement).value;
    }

    /** v1 textAngular toolbar active states — queryCommandState per selection move. */
    protected refreshCommandStates(): void {
        if (this.mode() !== 'html') return;
        const active = document.activeElement as HTMLElement | null;
        if (!active || !active.classList.contains('ta-bind')) return;
        const states: Record<string, boolean> = {};
        try {
            states['bold'] = document.queryCommandState('bold');
            states['italics'] = document.queryCommandState('italic');
            states['underline'] = document.queryCommandState('underline');
            states['ul'] = document.queryCommandState('insertUnorderedList');
            states['ol'] = document.queryCommandState('insertOrderedList');
        } catch {
            /* queryCommandState can throw on detached selections — leave states empty */
        }
        this.commandStates.set(states);
    }

    /**
     * v1 `insertToken`: `editor.wrapSelection('insertHTML', '{token}')` — string tokens insert
     * `{token}`, `{text, wrapper}` tokens insert `<wrapper>{text}</wrapper>`, at the editor caret.
     * The toggle/menu `mousedown` is prevented in the template so the editor keeps its selection;
     * the row's `.ta-bind` lives in the same `.modal-panel-input` as the clicked menu.
     */
    protected insertToken(ev: MouseEvent, token: any, row: { fieldData: any }): void {
        ev.preventDefault();
        const panel = (ev.target as HTMLElement).closest('.modal-panel-input');
        const editor = panel?.querySelector<HTMLElement>('.ta-bind[contenteditable]');
        const name = this.normalizedFields()[0]?.name;
        if (!editor || !name) return;
        const html =
            typeof token === 'string'
                ? '{' + token + '}'
                : token && token.wrapper
                    ? '<' + token.wrapper + '>{' + token.text + '}</' + token.wrapper + '>'
                    : '{' + (token?.text ?? token) + '}';
        editor.focus();
        document.execCommand('insertHTML', false, html);
        this.writeBack(editor, row.fieldData, name);
    }

    protected isRtl(language: any): boolean {
        if (!language) return false;
        if (language.isRtl !== undefined && language.isRtl !== null) {
            const v = language.isRtl;
            if (typeof v === 'string') {
                const s = v.toLowerCase();
                return s !== '' && s !== 'false' && s !== '0';
            }
            return !!v;
        }
        return this.rtlLanguages.indexOf(language.code) !== -1;
    }
}
